import {
  Type,
  type Static,
  type TArray,
  type TNull,
  type TNumber,
  type TOptional,
  type TString,
  type TUnion,
} from '@sinclair/typebox';
import { ProCampaignType } from './namespace';
import { BusinessStatsPeriod } from './stats';
import { BookingSource } from './translation_namespace/booking_source_namespace';
import { ProPaymentOrigin } from './translation_namespace/pro_payment_namespace';
import {
  StatsBreakdownDimension,
  StatsGranularity,
  StatsNudgeCode,
  StatsNudgeRemedy,
} from './translation_namespace/stats_namespace';

/**
 * Contrat API des statistiques métier du pro (`/app/stats/business/*`) — cf. plan « Statistiques
 * pro » §2 (conventions), §3 (indicateurs) et §4 (routes).
 *
 * Conventions :
 * - montants en couple `x` (chaîne formatée par le backend, `meta.is_price`) + `x_raw` (centimes) ;
 * - tableaux en `Type.Optional(Type.Array(…))` : `transformToSchema` rend `null` un tableau vide,
 *   le front lit `?? []` ;
 * - un chiffre réservé à une formule supérieure est servi `null` avec son drapeau `*_available`
 *   (jamais un 403 sur une route autorisée) ;
 * - les libellés de seaux / jours sont formatés côté backend (locale et fuseau du pro).
 */

const nullableNumber = (description?: string): TUnion<[TNumber, TNull]> =>
  Type.Union([Type.Number(), Type.Null()], description ? { description } : {});
const price = (sourceKey: string, description?: string): TString =>
  Type.String({ meta: { is_price: true, source_key: sourceKey }, ...(description ? { description } : {}) });

// ─── Fenêtre résolue ──────────────────────────────────────────────────────────

export const StatsWindowSchema = Type.Object({
  /** Période prédéfinie demandée ; `null` pour une plage personnalisée. */
  period: Type.Union([BusinessStatsPeriod, Type.Null()]),
  /** Premier jour local du pro (YYYY-MM-DD), inclus. */
  start_day: Type.String(),
  /** Dernier jour local du pro (YYYY-MM-DD), inclus. */
  end_day: Type.String(),
  /** Instant de début (UTC, ISO). */
  start: Type.String({ format: 'date-time' }),
  /** Instant de fin EXCLUSIF (UTC, ISO). */
  end: Type.String({ format: 'date-time' }),
  /** Libellés prêts à afficher (locale du pro). */
  start_formatted: Type.String(),
  end_formatted: Type.String(),
  /** Nombre de jours civils couverts. */
  days: Type.Integer(),
  includes_today: Type.Boolean(),
  /** Vrai pour « cette semaine » : la fenêtre contient des jours à venir. */
  includes_future: Type.Boolean(),
});
export type StatsWindowObject = Static<typeof StatsWindowSchema>;

// ─── Nudges « constat → remède » ─────────────────────────────────────────────

export const StatsNudgeSchema = Type.Object({
  code: Type.Enum(StatsNudgeCode),
  remedy: Type.Enum(StatsNudgeRemedy),
  /** Formule minimale apportant le remède ; `null` quand il est déjà inclus (CTA = réglage). */
  min_plan: Type.Union([Type.Literal('starter'), Type.Literal('pro'), Type.Literal('max'), Type.Null()]),
  /** Valeur du constat (taux, compte…) pour l'interpolation du libellé. */
  value: nullableNumber(),
});
export type StatsNudgeObject = Static<typeof StatsNudgeSchema>;

const nudges = (): TOptional<TArray<typeof StatsNudgeSchema>> => Type.Optional(Type.Array(StatsNudgeSchema));

// ─── Vue d'ensemble ───────────────────────────────────────────────────────────

/** Rendez-vous de la fenêtre (occurrences `appointment`, un participant de séance = un RDV). */
export const BusinessAppointmentsKpiSchema = Type.Object({
  /** Passés, confirmés, non marqués « non présenté ». */
  honored: Type.Integer(),
  /** Passés, confirmés, marqués « client non présenté ». */
  no_show: Type.Integer(),
  /** no_show / (honored + no_show) en %, `null` sans visite passée. */
  no_show_rate: nullableNumber(),
  cancelled_by_pro: Type.Integer(),
  cancelled_by_customer: Type.Integer(),
  /** annulés / (honorés + non présentés + annulés + demandes expirées) en %, `null` si 0. */
  cancellation_rate: nullableNumber(),
  /** Demandes passées jamais confirmées (WAITING_CONFIRMATION). */
  expired_requests: Type.Integer(),
  /** Réservations (lignes `reservation`) prises sur la fenêtre, datées par leur premier RDV créé. */
  bookings_created: Type.Integer(),
  /** RDV non annulés dont l'occurrence tombe dans la fenêtre (passés et à venir), non présentés compris. */
  planned: Type.Integer(),
  planned_no_show: Type.Integer(),
});
export type BusinessAppointmentsKpiObject = Static<typeof BusinessAppointmentsKpiSchema>;

export const BusinessRevenueKpiSchema = Type.Object({
  /** CA net encaissé (paiements encaissés − remboursements), TTC, frais Stripe non déduits. */
  collected: price('collected_raw'),
  collected_raw: Type.Number(),
  refunded: price('refunded_raw'),
  refunded_raw: Type.Number(),
  /** Paiements au net > 0 (dénominateur du panier moyen). */
  payments_count: Type.Integer(),
  average_basket: price('average_basket_raw'),
  average_basket_raw: Type.Number(),
  /** « CA prévu » : valeur au prix réservé des RDV confirmés de la fenêtre, hors non présentés. */
  planned: price('planned_raw'),
  planned_raw: Type.Number(),
  /** Au moins un RDV prévu sans prix connu (devis, option « à partir de » sans montant). */
  has_unpriced: Type.Boolean(),
});
export type BusinessRevenueKpiObject = Static<typeof BusinessRevenueKpiSchema>;

export const BusinessCustomersKpiSchema = Type.Object({
  /** Fiches clients entrées dans la base sur la fenêtre (`customer_since`). */
  new_count: Type.Integer(),
});
export type BusinessCustomersKpiObject = Static<typeof BusinessCustomersKpiSchema>;

/** Instantané (indépendant de la fenêtre) : les 12 prochains mois. */
export const BusinessUpcomingSchema = Type.Object({
  /** RDV confirmés ou en attente à partir de maintenant. */
  count: Type.Integer(),
  to_confirm_count: Type.Integer(),
  /** Reste à encaisser sur les RDV confirmés à venir (valeur réservée − déjà encaissé ou couvert par un bon). */
  remaining_to_collect: price('remaining_to_collect_raw'),
  remaining_to_collect_raw: Type.Number(),
  /** Même calcul sur les demandes en attente de confirmation. */
  to_confirm_remaining: price('to_confirm_remaining_raw'),
  to_confirm_remaining_raw: Type.Number(),
});
export type BusinessUpcomingObject = Static<typeof BusinessUpcomingSchema>;

export const BusinessOverviewTrendSchema = Type.Object({
  honored: nullableNumber(),
  no_show_rate: nullableNumber(),
  cancelled: nullableNumber(),
  bookings_created: nullableNumber(),
  collected: nullableNumber(),
  average_basket: nullableNumber(),
  planned_revenue: nullableNumber(),
  new_customers: nullableNumber(),
});
export type BusinessOverviewTrendObject = Static<typeof BusinessOverviewTrendSchema>;

export const BusinessOverviewComparisonSchema = Type.Object({
  previous_window: StatsWindowSchema,
  previous: Type.Object({
    appointments: BusinessAppointmentsKpiSchema,
    revenue: BusinessRevenueKpiSchema,
    customers: BusinessCustomersKpiSchema,
  }),
  /** Variation en % vs la période précédente adjacente de même durée ; `null` si base nulle. */
  trend: BusinessOverviewTrendSchema,
});
export type BusinessOverviewComparisonObject = Static<typeof BusinessOverviewComparisonSchema>;

export const BusinessOverviewResponseSchema = Type.Object({
  window: StatsWindowSchema,
  appointments: BusinessAppointmentsKpiSchema,
  revenue: BusinessRevenueKpiSchema,
  customers: BusinessCustomersKpiSchema,
  upcoming: BusinessUpcomingSchema,
  /** Faux sans `stats_advanced.read` (Starter) : la comparaison est alors `null`. */
  comparison_available: Type.Boolean(),
  comparison: Type.Union([BusinessOverviewComparisonSchema, Type.Null()]),
  nudges: nudges(),
});
export type BusinessOverviewResponseObject = Static<typeof BusinessOverviewResponseSchema>;

// ─── Série temporelle ─────────────────────────────────────────────────────────

export const BusinessTimeseriesPointSchema = Type.Object({
  /** Début du seau, jour local du pro (YYYY-MM-DD). */
  bucket: Type.String(),
  /** Libellé court prêt à afficher (« 12 sept. », « sem. du 7 sept. », « sept. 2026 »). */
  bucket_formatted: Type.String(),
  bucket_start_formatted: Type.String(),
  bucket_end_formatted: Type.String(),
  /** Premier ou dernier seau tronqué par les bornes de la fenêtre. */
  partial: Type.Boolean(),
  honored: Type.Integer(),
  bookings_created: Type.Integer(),
  revenue: price('revenue_raw'),
  revenue_raw: Type.Number(),
  /** Unités majeures (échelle des axes), calculées côté backend selon la devise. */
  revenue_major: Type.Number(),
});
export type BusinessTimeseriesPointObject = Static<typeof BusinessTimeseriesPointSchema>;

export const BusinessTimeseriesResponseSchema = Type.Object({
  window: StatsWindowSchema,
  granularity: Type.Enum(StatsGranularity),
  points: Type.Optional(Type.Array(BusinessTimeseriesPointSchema)),
});
export type BusinessTimeseriesResponseObject = Static<typeof BusinessTimeseriesResponseSchema>;

// ─── Agenda : statuts, heures de pointe, provenance ───────────────────────────

export const BusinessHeatmapCellSchema = Type.Object({
  /** Jour ISO : 1 = lundi … 7 = dimanche. */
  dow: Type.Integer({ minimum: 1, maximum: 7 }),
  hour: Type.Integer({ minimum: 0, maximum: 23 }),
  count: Type.Integer(),
});
export type BusinessHeatmapCellObject = Static<typeof BusinessHeatmapCellSchema>;

export const BusinessBookingSourceItemSchema = Type.Object({
  /** `null` = non renseigné (antérieur à l'instrumentation). */
  source: Type.Union([Type.Enum(BookingSource), Type.Null()]),
  label: Type.String(),
  count: Type.Integer(),
  /** Part en % des réservations prises sur la fenêtre. */
  share: Type.Number(),
});
export type BusinessBookingSourceItemObject = Static<typeof BusinessBookingSourceItemSchema>;

export const BusinessAgendaResponseSchema = Type.Object({
  window: StatsWindowSchema,
  statuses: Type.Object({
    honored: Type.Integer(),
    no_show: Type.Integer(),
    cancelled_by_pro: Type.Integer(),
    cancelled_by_customer: Type.Integer(),
    expired_requests: Type.Integer(),
  }),
  /** Faux sans `stats_team.read` (Max) : la heatmap est alors `null`. */
  heatmap_available: Type.Boolean(),
  heatmap: Type.Union([Type.Array(BusinessHeatmapCellSchema), Type.Null()]),
  sources: Type.Optional(Type.Array(BusinessBookingSourceItemSchema)),
  nudges: nudges(),
});
export type BusinessAgendaResponseObject = Static<typeof BusinessAgendaResponseSchema>;

// ─── Clients ──────────────────────────────────────────────────────────────────

export const BusinessCustomersSummarySchema = Type.Object({
  new_count: Type.Integer(),
  /** Clients avec au moins une visite honorée sur la fenêtre. */
  active_count: Type.Integer(),
  /** Part des clients actifs ayant déjà un prochain RDV, en %. `null` sans actif. */
  rebooking_rate: nullableNumber(),
  /**
   * Sans RDV à venir, par ancienneté de la dernière visite. Tranches EXCLUSIVES : pour « pas revenus
   * depuis N mois », additionner toutes les tranches au-delà du seuil (3 mois = days_90_180 +
   * days_180_plus), jamais la tranche en deçà.
   */
  inactive: Type.Object(
    {
      days_30_90: Type.Integer({ description: 'Dernière visite il y a 30 à 90 jours (moins de 3 mois).' }),
      days_90_180: Type.Integer({ description: 'Dernière visite il y a 90 à 180 jours (3 à 6 mois).' }),
      days_180_plus: Type.Integer({ description: 'Dernière visite il y a plus de 180 jours (plus de 6 mois).' }),
    },
    {
      description:
        'Clients sans rendez-vous à venir, par ancienneté de la dernière visite. Tranches exclusives : « pas revenus depuis 3 mois » = days_90_180 + days_180_plus (jamais days_30_90).',
    },
  ),
});
export type BusinessCustomersSummaryObject = Static<typeof BusinessCustomersSummarySchema>;

export const BusinessFirstVisitPointSchema = Type.Object({
  bucket: Type.String(),
  bucket_formatted: Type.String(),
  /** Clients dont la toute première visite honorée tombe dans le seau. */
  first_visit: Type.Integer(),
  /** Clients déjà venus avant. */
  returning: Type.Integer(),
});
export type BusinessFirstVisitPointObject = Static<typeof BusinessFirstVisitPointSchema>;

export const BusinessTopCustomerSchema = Type.Object({
  pro_customer_id: Type.Integer(),
  name: Type.String(),
  /** Visites honorées, vie entière. */
  visits_count: Type.Integer(),
  last_visit: Type.Optional(Type.String({ format: 'date-time' })),
  last_visit_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'last_visit' } })),
  /** CA net encaissé vie entière — absent sans `stats_advanced.read`. */
  revenue: Type.Optional(price('revenue_raw')),
  revenue_raw: Type.Optional(Type.Number()),
});
export type BusinessTopCustomerObject = Static<typeof BusinessTopCustomerSchema>;

export const BusinessCustomersResponseSchema = Type.Object({
  window: StatsWindowSchema,
  granularity: Type.Enum(StatsGranularity),
  summary: BusinessCustomersSummarySchema,
  first_visits: Type.Optional(Type.Array(BusinessFirstVisitPointSchema)),
  /** Faux sans `stats_advanced.read` : `revenue` absent des meilleurs clients. */
  revenue_available: Type.Boolean(),
  top_customers: Type.Optional(Type.Array(BusinessTopCustomerSchema)),
  nudges: nudges(),
});
export type BusinessCustomersResponseObject = Static<typeof BusinessCustomersResponseSchema>;

// ─── Ventes : encaissé par origine, cartes cadeaux ────────────────────────────

export const BusinessSalesOriginItemSchema = Type.Object({
  origin: Type.Enum(ProPaymentOrigin),
  label: Type.String(),
  amount: price('amount_raw'),
  amount_raw: Type.Number(),
  count: Type.Integer(),
});
export type BusinessSalesOriginItemObject = Static<typeof BusinessSalesOriginItemSchema>;

export const BusinessVouchersSchema = Type.Object({
  /** Bons vendus en ligne ou créés manuellement sur la fenêtre (paniers non payés exclus, recharges non comptées). */
  sold: Type.Object({
    count: Type.Integer(),
    manual_count: Type.Integer(),
    amount: price('amount_raw'),
    amount_raw: Type.Number(),
  }),
  /** Montant débité des bons AMOUNT sur la fenêtre (restitutions exclues) ; usages tous types. */
  consumed: Type.Object({
    amount: price('amount_raw'),
    amount_raw: Type.Number(),
    usages_count: Type.Integer(),
  }),
  /** Solde restant des bons actifs non expirés (instantané) : service encaissé encore dû. */
  outstanding: Type.Object({
    amount: price('amount_raw'),
    amount_raw: Type.Number(),
    remaining_sessions: Type.Integer(),
  }),
  /** Bons expirés sur la partie passée de la fenêtre avec un solde restant. */
  expired: Type.Object({
    count: Type.Integer(),
    lost_amount: price('lost_amount_raw'),
    lost_amount_raw: Type.Number(),
  }),
});
export type BusinessVouchersObject = Static<typeof BusinessVouchersSchema>;

export const BusinessSalesComparisonSchema = Type.Object({
  previous_window: StatsWindowSchema,
  previous: BusinessRevenueKpiSchema,
  trend: Type.Object({
    collected: nullableNumber(),
    average_basket: nullableNumber(),
    payments_count: nullableNumber(),
  }),
});
export type BusinessSalesComparisonObject = Static<typeof BusinessSalesComparisonSchema>;

export const BusinessSalesResponseSchema = Type.Object({
  window: StatsWindowSchema,
  revenue: BusinessRevenueKpiSchema,
  by_origin: Type.Optional(Type.Array(BusinessSalesOriginItemSchema)),
  vouchers: BusinessVouchersSchema,
  comparison_available: Type.Boolean(),
  comparison: Type.Union([BusinessSalesComparisonSchema, Type.Null()]),
});
export type BusinessSalesResponseObject = Static<typeof BusinessSalesResponseSchema>;

// ─── Avis Google ──────────────────────────────────────────────────────────────

export const BusinessReviewsPlaceSchema = Type.Object({
  /** `null` pour une fiche Google non reliée à un lieu. */
  pro_place_id: Type.Union([Type.Integer(), Type.Null()]),
  label: Type.String(),
  average_rating: nullableNumber(),
  total_reviews: Type.Integer(),
});
export type BusinessReviewsPlaceObject = Static<typeof BusinessReviewsPlaceSchema>;

export const BusinessReviewsResponseSchema = Type.Object({
  window: StatsWindowSchema,
  /** Au moins une fiche Google connectée. */
  connected: Type.Boolean(),
  current: Type.Object({
    /** Moyenne des fiches pondérée par leur nombre d'avis. */
    average_rating: nullableNumber(),
    total_reviews: Type.Integer(),
    /** Part des avis notés ayant une réponse publiée, en %. */
    reply_rate: nullableNumber(),
    /** Avis à traiter (nouveaux ou avec réponse suggérée). */
    to_reply_count: Type.Integer(),
  }),
  period: Type.Object({
    new_reviews: Type.Integer(),
    trend: nullableNumber(),
    /** Note moyenne des seuls avis reçus sur la fenêtre. */
    average_rating: nullableNumber(),
  }),
  distribution: Type.Optional(
    Type.Array(Type.Object({ stars: Type.Integer({ minimum: 1, maximum: 5 }), count: Type.Integer() })),
  ),
  granularity: Type.Enum(StatsGranularity),
  timeseries: Type.Optional(
    Type.Array(Type.Object({ bucket: Type.String(), bucket_formatted: Type.String(), new_reviews: Type.Integer() })),
  ),
  /** Faux sans `stats_advanced.read` : `places` est alors `null`. */
  places_available: Type.Boolean(),
  places: Type.Union([Type.Array(BusinessReviewsPlaceSchema), Type.Null()]),
  nudges: nudges(),
});
export type BusinessReviewsResponseObject = Static<typeof BusinessReviewsResponseSchema>;

// ─── Accueil : Aujourd'hui, Cette semaine, À traiter ──────────────────────────

export const BusinessHomeResponseSchema = Type.Object({
  today: Type.Object({
    date_formatted: Type.String(),
    /** RDV valides non annulés du jour local (non présentés et participants compris). */
    appointments_count: Type.Integer(),
    group_sessions_count: Type.Integer(),
    no_show_count: Type.Integer(),
    to_confirm_count: Type.Integer(),
    next_appointment: Type.Union([
      Type.Object({
        appointment_id: Type.Integer(),
        /** Heure locale prête à afficher (« 14:30 »). */
        time_formatted: Type.String(),
        label: Type.String(),
      }),
      Type.Null(),
    ]),
  }),
  week: Type.Object({
    /** « Du 28 sept. au 4 oct. » */
    range_formatted: Type.String(),
    planned_count: Type.Integer(),
    planned_no_show_count: Type.Integer(),
    revenue_planned: price('revenue_planned_raw'),
    revenue_planned_raw: Type.Number(),
  }),
  to_handle: Type.Object({
    /** Demandes de RDV à confirmer, à venir. */
    waiting_confirmation_count: Type.Integer(),
    first_waiting_start: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    first_waiting_formatted: Type.Optional(
      Type.String({ format: 'date-time', meta: { source_key: 'first_waiting_start', with_time: true } }),
    ),
    /** `null` sans accès aux avis Google. */
    reviews_to_reply_count: Type.Union([Type.Integer(), Type.Null()]),
    contact_requests_unread_count: Type.Integer(),
  }),
});
export type BusinessHomeResponseObject = Static<typeof BusinessHomeResponseSchema>;

// ─── Taux d'occupation (Max) ──────────────────────────────────────────────────

export const BusinessOccupancyResponseSchema = Type.Object({
  window: StatsWindowSchema,
  /** Faux si aucune minute d'ouverture ou fenêtre > 93 jours. */
  available: Type.Boolean(),
  /** Minutes occupées / minutes d'ouverture (membre-heures), en %, plafonné à 100. */
  rate: nullableNumber(),
  busy_minutes: Type.Integer(),
  open_minutes: Type.Integer(),
  nudges: nudges(),
});
export type BusinessOccupancyResponseObject = Static<typeof BusinessOccupancyResponseSchema>;

// ─── Détail par prestation / membre / lieu ────────────────────────────────────

export const BusinessBreakdownItemSchema = Type.Object({
  id: Type.Integer(),
  label: Type.String(),
  honored_count: Type.Integer(),
  no_show_count: Type.Integer(),
  cancelled_count: Type.Integer(),
  /** Valeur au prix réservé des RDV honorés (parts égales entre membres). Ce n'est pas l'encaissé. */
  value: price('value_raw'),
  value_raw: Type.Number(),
  /** Part en % des RDV honorés de la fenêtre. */
  share: Type.Number(),
});
export type BusinessBreakdownItemObject = Static<typeof BusinessBreakdownItemSchema>;

export const BusinessBreakdownResponseSchema = Type.Object({
  window: StatsWindowSchema,
  dimension: Type.Enum(StatsBreakdownDimension),
  items: Type.Optional(Type.Array(BusinessBreakdownItemSchema)),
});
export type BusinessBreakdownResponseObject = Static<typeof BusinessBreakdownResponseSchema>;

// ─── Campagnes email / SMS ────────────────────────────────────────────────────

export const BusinessCampaignAttributionSchema = Type.Object({
  /** Réservations prises en suivant un lien de la campagne. */
  bookings_count: Type.Integer(),
  revenue: price('revenue_raw'),
  revenue_raw: Type.Number(),
  customers_count: Type.Integer(),
});
export type BusinessCampaignAttributionObject = Static<typeof BusinessCampaignAttributionSchema>;

export const BusinessCampaignItemSchema = Type.Object({
  pro_campaign_id: Type.Integer(),
  name: Type.String(),
  type: Type.Enum(ProCampaignType),
  sent_at: Type.String({ format: 'date-time' }),
  sent_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'sent_at', with_time: true } }),
  ),
  recipients_count: Type.Integer(),
  /** Compteurs remontés par Mailjet / AllMySMS ; `null` tant que la synchronisation n'a pas eu lieu. */
  delivered_count: Type.Union([Type.Integer(), Type.Null()]),
  opened_count: Type.Union([Type.Integer(), Type.Null()]),
  clicked_count: Type.Union([Type.Integer(), Type.Null()]),
  open_rate: nullableNumber(),
  click_rate: nullableNumber(),
  /** `null` sans `stats_advanced.read`. */
  attribution: Type.Union([BusinessCampaignAttributionSchema, Type.Null()]),
});
export type BusinessCampaignItemObject = Static<typeof BusinessCampaignItemSchema>;

export const BusinessCampaignsResponseSchema = Type.Object({
  window: StatsWindowSchema,
  summary: Type.Object({
    sent_count: Type.Integer(),
    email_count: Type.Integer(),
    sms_count: Type.Integer(),
    recipients_count: Type.Integer(),
    delivered_count: Type.Integer(),
    opened_count: Type.Integer(),
    clicked_count: Type.Integer(),
    open_rate: nullableNumber(),
    click_rate: nullableNumber(),
    credits: Type.Object({ email_used: Type.Integer(), sms_used: Type.Integer() }),
    unsubscribes: Type.Object({ email: Type.Integer(), sms: Type.Integer() }),
    /** Date depuis laquelle les compteurs bruts (ouvertures, clics) sont disponibles. */
    counters_since_formatted: Type.String(),
  }),
  attribution_available: Type.Boolean(),
  attribution: Type.Union([BusinessCampaignAttributionSchema, Type.Null()]),
  items: Type.Optional(Type.Array(BusinessCampaignItemSchema)),
});
export type BusinessCampaignsResponseObject = Static<typeof BusinessCampaignsResponseSchema>;
